/**
 * IntegrationsSection — Batch #14.62
 *
 * Tableau de bord READ-ONLY des intégrations externes (Stripe, Drive, SMTP, Firebase, etc.).
 *
 * Principe : on n'affiche JAMAIS les clés (que les 4 derniers caractères).
 * Pour modifier une intégration, il faut passer par la console Emergent.
 */
import { useEffect, useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Plug, CreditCard, Cloud, Mail, Bell, Database,
  CheckCircle2, XCircle, ExternalLink, PlayCircle, Loader2, RefreshCw, Lock,
} from "lucide-react";

const ICONS = {
  "credit-card": CreditCard,
  cloud: Cloud,
  mail: Mail,
  bell: Bell,
  database: Database,
};

export default function IntegrationsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({}); // {integrationId: bool}
  const [results, setResults] = useState({}); // {integrationId: {ok, message}}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/integrations/status");
      setData(r.data);
    } catch (e) {
      toast.error("Impossible de charger les intégrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const testIntegration = async (id) => {
    setTesting((t) => ({ ...t, [id]: true }));
    setResults((r) => ({ ...r, [id]: null }));
    try {
      const r = await api.post(`/admin/integrations/${id}/test`);
      setResults((res) => ({ ...res, [id]: r.data }));
      if (r.data.ok) toast.success(`${id.toUpperCase()} : connexion réussie`);
      else toast.error(`${id.toUpperCase()} : ${r.data.message || "échec"}`);
    } catch (e) {
      setResults((res) => ({ ...res, [id]: { ok: false, message: e.response?.data?.detail || e.message } }));
    } finally {
      setTesting((t) => ({ ...t, [id]: false }));
    }
  };

  if (loading && !data) {
    return <div className="bg-white border rounded-xl p-6 text-sm text-slate-500">Chargement…</div>;
  }
  if (!data) return null;

  // Group by category
  const grouped = data.integrations.reduce((acc, i) => {
    (acc[i.category] = acc[i.category] || []).push(i);
    return acc;
  }, {});

  const { total, configured, missing } = data.summary;

  return (
    <div className="space-y-5" data-testid="integrations-section">
      {/* Header */}
      <div className="bg-white border rounded-xl p-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Plug className="w-4 h-4" /> Mes intégrations
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Tableau de bord des services externes. Pour modifier une clé, utilise la console
              <strong> Emergent → Deployment → Environment Variables</strong>.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="integrations-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4">
          <StatCard icon={Plug} label="Total" value={total} color="slate" />
          <StatCard icon={CheckCircle2} label="Configurées" value={configured} color="emerald" />
          <StatCard icon={XCircle} label="Manquantes" value={missing} color={missing > 0 ? "rose" : "slate"} />
        </div>

        {/* Security note */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <Lock className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-900 leading-relaxed">
            <strong>Sécurité :</strong> les clés ne sont jamais affichées en clair. Tu vois uniquement
            les 4 derniers caractères pour identifier visuellement laquelle est en cours d'utilisation.
            <strong> Aucune clé n'est stockée dans la base de données.</strong>
          </p>
        </div>
      </div>

      {/* Integrations grouped by category */}
      {Object.entries(grouped).map(([category, items]) => (
        <div key={category}>
          <h4 className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2 pl-1">
            {category}
          </h4>
          <div className="space-y-3">
            {items.map((integ) => {
              const Icon = ICONS[integ.icon] || Plug;
              const r = results[integ.id];
              return (
                <div
                  key={integ.id}
                  className={`bg-white border rounded-xl p-4 ${integ.configured ? "border-emerald-200" : "border-rose-200 bg-rose-50/30"}`}
                  data-testid={`integration-${integ.id}`}
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      integ.configured ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{integ.name}</span>
                        {integ.configured ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-0.5">
                            <CheckCircle2 className="w-3 h-3" /> Configurée
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 inline-flex items-center gap-0.5">
                            <XCircle className="w-3 h-3" /> Manquante
                          </span>
                        )}
                      </div>

                      {/* Fields with masked values */}
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {integ.fields.map((f) => (
                          <div key={f.key} className="text-[11px] flex items-baseline gap-1.5">
                            <span className="text-slate-500 truncate">{f.label}:</span>
                            <code className={`font-mono ${f.mask ? "text-slate-700" : "text-rose-600"}`}>
                              {f.mask || "(vide)"}
                            </code>
                          </div>
                        ))}
                      </div>

                      {/* Test result */}
                      {r && (
                        <div className={`mt-2 text-[11px] px-2 py-1 rounded-md ${
                          r.ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                               : "bg-rose-50 text-rose-700 border border-rose-200"
                        }`}>
                          {r.ok ? "✓ " : "✗ "}{r.message}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {integ.testable && integ.configured && (
                      <button
                        onClick={() => testIntegration(integ.id)}
                        disabled={testing[integ.id]}
                        className="text-xs px-2.5 py-1 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`integration-${integ.id}-test`}
                      >
                        {testing[integ.id] ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <PlayCircle className="w-3 h-3" />
                        )}
                        Tester
                      </button>
                    )}
                    {integ.doc_url && (
                      <a
                        href={integ.doc_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"
                        data-testid={`integration-${integ.id}-doc`}
                      >
                        <ExternalLink className="w-3 h-3" /> Console
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colorMap = {
    slate:   "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose:    "bg-rose-50 border-rose-200 text-rose-700",
  };
  return (
    <div className={`border rounded-lg p-3 ${colorMap[color] || colorMap.slate}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-[11px] font-semibold">{label}</span>
      </div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}
